import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    if (videoElement.hasAttribute('autoplay')) {
      this.#playWhenReady(videoElement);
    }
  }

  /**
   * Ensure autoplay actually starts after runtime source injection.
   * Some browsers do not reliably honor the autoplay attribute alone after sources are assigned via JavaScript.
   * @param {HTMLVideoElement} videoElement
   */
  #playWhenReady(videoElement) {
    const tryPlay = () => {
      const playPromise = videoElement.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };

    if (videoElement.readyState >= 2) {
      tryPlay();
      return;
    }

    const handleCanPlay = () => {
      videoElement.removeEventListener('canplay', handleCanPlay);
      tryPlay();
    };

    videoElement.addEventListener('canplay', handleCanPlay, { once: true });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
